/* shoppingVoiceParser — pure, framework-free parsing + merge helpers for
 * the Shopping List voice commands.  Pulled out of ShoppingList.jsx so
 * the regex zoo can grow without polluting the component and so the
 * logic is independently testable (plain Node, no React).
 *
 * Public surface:
 *   splitChained(text) → string[]
 *   parseOne(text) → { qty, unit, name }
 *   normaliseStem(name) → string          // soft dedupe key
 *   getUnitCategory(unit) → "mass"|"volume"|"count"|null
 *   canMerge(unitA, unitB) → bool         // same category OR both bare
 *   convertQty(qty, fromUnit, toUnit) → number|null
 *   findMergeTarget(items, parsed) → item|null
 *   formatQtyPhrase(qty, unit, name) → string  // for TTS/UI
 *   mergeTotals(existingQty, existingUnit, newQty, newUnit)
 *       → { qty, unit } | null
 *
 *  Unit scope (user requested):
 *   • mass:    g ↔ kg, oz ↔ lb
 *   • volume:  ml ↔ l (litre / liter / litres / liters)
 *   • count:   bare numbers (no unit)
 *  Culinary spoon units (cup/tbsp/tsp) are *not* auto-converted — users
 *  almost never want "3.17 cups + 2 tbsp = 3.29 cups" so we treat them
 *  as discrete-same-unit only (merge if identical, otherwise keep both).
 */

// -------- chained utterances --------------------------------------------
// Longest-first so "and then" wins over bare "and" and compound-noun
// mentions like "sweet and sour sauce" slip through the final `and`
// delimiter — that's a known edge case documented in the help pages.
const CHAIN_DELIMS = [
  /\s+and\s+then\s+/i,
  /\s*,\s*and\s+/i,
  /\s*,\s+/i,
  /\s*;\s+/i,
  /\s+plus\s+/i,
  /\s+also\s+/i,
  /\s+and\s+/i,
];

export function splitChained(s) {
  let parts = [s];
  for (const delim of CHAIN_DELIMS) {
    parts = parts.flatMap((p) => p.split(delim)).map((p) => p.trim()).filter(Boolean);
  }
  return parts;
}

// -------- single-item parser --------------------------------------------
const WORD_QTY = [
  ["half a dozen", { qty: 6, unit: "" }],
  ["a dozen",      { qty: 12, unit: "" }],
  ["a couple of",  { qty: 2, unit: "" }],
  ["a couple",     { qty: 2, unit: "" }],
  ["a few",        { qty: 3, unit: "" }],
  ["a pair of",    { qty: 2, unit: "" }],
  ["a loaf of",    { qty: 1, unit: "loaf" }],
  ["a bunch of",   { qty: 1, unit: "bunch" }],
  ["a bag of",     { qty: 1, unit: "bag" }],
  ["a packet of",  { qty: 1, unit: "packet" }],
  ["a pack of",    { qty: 1, unit: "pack" }],
  ["a bottle of",  { qty: 1, unit: "bottle" }],
  ["a jar of",     { qty: 1, unit: "jar" }],
  ["a tin of",     { qty: 1, unit: "tin" }],
  ["a can of",     { qty: 1, unit: "can" }],
  ["a box of",     { qty: 1, unit: "box" }],
  ["a punnet of",  { qty: 1, unit: "punnet" }],
  ["a carton of",  { qty: 1, unit: "carton" }],
  ["a block of",   { qty: 1, unit: "block" }],
  ["a head of",    { qty: 1, unit: "head" }],
  ["a clove of",   { qty: 1, unit: "clove" }],
  ["a sprig of",   { qty: 1, unit: "sprig" }],
  ["a slice of",   { qty: 1, unit: "slice" }],
  ["a knob of",    { qty: 1, unit: "knob" }],
  ["a pinch of",   { qty: 1, unit: "pinch" }],
  ["a splash of",  { qty: 1, unit: "splash" }],
  ["a dash of",    { qty: 1, unit: "dash" }],
  ["a handful of", { qty: 1, unit: "handful" }],
];

const UNIT_PATTERN = /^(\d+(?:\.\d+)?)\s*(litres?|liters?|ml|kg|g|oz|lb|pounds?|cups?|tbsp|tsp)?\s+(?:of\s+)?(.+)$/i;

export function parseOne(raw) {
  let name = String(raw || "").trim();
  for (const [phrase, meta] of WORD_QTY) {
    if (name.toLowerCase().startsWith(phrase + " ")) {
      return { qty: meta.qty, unit: meta.unit, name: name.slice(phrase.length + 1).trim() };
    }
  }
  const m = name.match(UNIT_PATTERN);
  if (m) {
    return {
      qty: Number(m[1]),
      unit: canonicaliseUnit(m[2] || ""),
      name: m[3].trim(),
    };
  }
  name = name.replace(/^(?:some\s+|a\s+|an\s+|the\s+)/i, "").trim();
  return { qty: null, unit: "", name };
}

// -------- units ----------------------------------------------------------
export function canonicaliseUnit(u) {
  const x = String(u || "").toLowerCase().trim();
  if (!x) return "";
  if (/^(litre|litres|liter|liters|l)$/.test(x)) return "litre";
  if (x === "ml") return "ml";
  if (x === "kg") return "kg";
  if (x === "g") return "g";
  if (/^(lb|lbs|pound|pounds)$/.test(x)) return "lb";
  if (/^oz$/.test(x)) return "oz";
  if (/^cups?$/.test(x)) return "cup";
  if (x === "tbsp") return "tbsp";
  if (x === "tsp") return "tsp";
  return x.replace(/s$/, "");
}

const UNIT_CATEGORY = {
  g: "mass", kg: "mass", oz: "mass", lb: "mass",
  ml: "volume", litre: "volume",
  cup: "cup", tbsp: "tbsp", tsp: "tsp",
};

export function getUnitCategory(u) {
  const unit = canonicaliseUnit(u);
  if (!unit) return "count";
  return UNIT_CATEGORY[unit] || "other";
}

// Grams (mass) / millilitres (volume) are the canonical bases.
const TO_BASE = {
  g: 1, kg: 1000,
  oz: 28.3495, lb: 453.592,
  ml: 1, litre: 1000,
};

export function canMerge(a, b) {
  const ca = getUnitCategory(a);
  const cb = getUnitCategory(b);
  if (ca === "count" && cb === "count") return true;
  if (ca === "mass" && cb === "mass") return true;
  if (ca === "volume" && cb === "volume") return true;
  // Spoon/cup units only merge with themselves (no conversion).
  if (ca === cb && (ca === "cup" || ca === "tbsp" || ca === "tsp")) {
    return canonicaliseUnit(a) === canonicaliseUnit(b);
  }
  if (ca === "other" && cb === "other") {
    return canonicaliseUnit(a) === canonicaliseUnit(b);
  }
  return false;
}

export function convertQty(qty, fromUnit, toUnit) {
  if (qty == null) return null;
  const f = canonicaliseUnit(fromUnit);
  const t = canonicaliseUnit(toUnit);
  if (f === t) return qty;
  if (TO_BASE[f] && TO_BASE[t]) {
    return (qty * TO_BASE[f]) / TO_BASE[t];
  }
  return null;
}

// -------- stems ----------------------------------------------------------
// Soft dedupe key: trim, lowercase, strip trailing filler like "to taste",
// collapse double spaces, drop a trailing "s" so "apple" ≈ "apples",
// "egg" ≈ "eggs". Deliberately conservative — doesn't try to be a full
// stemmer; the backend normalise() will catch the rest when we POST.
export function normaliseStem(name) {
  const s = String(name || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!s) return "";
  // Only strip trailing s on single-word items so "bananas" → "banana"
  // but "mixed herbs" stays.
  if (!s.includes(" ") && s.endsWith("s") && s.length > 3) return s.slice(0, -1);
  return s;
}

// -------- merge target lookup -------------------------------------------
// Returns the first unchecked, non-staple item on the list that shares
// a stem with `parsed.name` AND has a unit that can be merged with
// parsed.unit, or null if none.
export function findMergeTarget(items, parsed) {
  const stem = normaliseStem(parsed.name);
  if (!stem) return null;
  const list = (items || []).filter((i) => !i.checked && !i.is_staple);
  // Prefer exact stem match on display_name, then normalised_name,
  // then substring match (so "milk" finds "whole milk" and vice versa).
  const score = (it) => {
    const dn = normaliseStem(it.display_name || "");
    const nn = normaliseStem(it.normalised_name || "");
    if (dn === stem || nn === stem) return 3;
    if (dn.includes(stem) || stem.includes(dn)) return 2;
    if (nn.includes(stem) || stem.includes(nn)) return 2;
    return 0;
  };
  let best = null; let bestScore = 0;
  for (const it of list) {
    const s = score(it);
    if (s === 0) continue;
    if (!canMerge(it.unit || "", parsed.unit || "")) continue;
    if (s > bestScore) { best = it; bestScore = s; }
  }
  return best;
}

// -------- display + TTS phrasing ----------------------------------------
const UNIT_WORDS = {
  g: "grams", kg: "kilos",
  oz: "ounces", lb: "pounds",
  ml: "millilitres", litre: "litres",
  cup: "cups", tbsp: "tablespoons", tsp: "teaspoons",
};

// Smart pluralise + humanise. "1 litre of milk", "2 litres of milk",
// "3 apples", "1 loaf of bread".
export function formatQtyPhrase(qty, unit, name) {
  const u = canonicaliseUnit(unit);
  const n = (name || "").trim();
  if (qty == null) return n;
  const roundQty = Math.round(qty * 100) / 100;
  if (!u) {
    // bare count — "1 apple" / "3 apples"
    const noun = roundQty === 1 ? (n.endsWith("s") && n.length > 3 ? n.slice(0, -1) : n) : (n.endsWith("s") ? n : `${n}s`);
    return `${roundQty} ${noun}`;
  }
  const word = UNIT_WORDS[u] || u;
  const unitPhrase = roundQty === 1 ? singular(word) : word;
  return `${roundQty} ${unitPhrase} of ${n}`;
}

function singular(word) {
  if (word === "grams") return "gram";
  if (word === "kilos") return "kilo";
  if (word === "ounces") return "ounce";
  if (word === "pounds") return "pound";
  if (word === "millilitres") return "millilitre";
  if (word === "litres") return "litre";
  if (word === "cups") return "cup";
  if (word === "tablespoons") return "tablespoon";
  if (word === "teaspoons") return "teaspoon";
  if (word.endsWith("s") && word.length > 3) return word.slice(0, -1);
  return word;
}

// -------- merge totals ---------------------------------------------------
// Returns { qty, unit } to write back to the existing item on "add".
// Prefers to keep the existing item's unit (so "1 litre + 2 litres = 3
// litres" and "500 g + 1 kg = 1500 g" — simpler than re-upgrading to
// the next SI prefix). Returns null if units aren't mergeable.
export function mergeTotals(existingQty, existingUnit, newQty, newUnit) {
  if (!canMerge(existingUnit, newUnit)) return null;
  if (existingQty == null && newQty == null) return { qty: null, unit: existingUnit || newUnit || "" };
  if (existingQty == null) return { qty: newQty, unit: existingUnit || newUnit || "" };
  if (newQty == null) return { qty: existingQty, unit: existingUnit || newUnit || "" };
  const converted = convertQty(newQty, newUnit, existingUnit);
  if (converted == null) return null;
  return { qty: Math.round((existingQty + converted) * 1000) / 1000, unit: canonicaliseUnit(existingUnit || newUnit || "") };
}

// -------- strip voice "add ... to the list" shell -----------------------
export function stripAddShell(txt) {
  return String(txt || "")
    .replace(/^.*?\badd\s+/i, "")
    .replace(/\s+to\s+(?:the\s+|my\s+)?(?:shopping\s+)?list\s*\.?$/i, "")
    .trim();
}

// -------- merge-prompt decision parser ----------------------------------
// Robust single-utterance classifier for the "add / replace / delete /
// cancel" follow-up. Picks the first unambiguous intent in the string.
// Returns one of: "add" | "replace" | "delete" | "cancel" | null.
export function parseMergeDecision(txt) {
  const s = String(txt || "").toLowerCase();
  if (!s.trim()) return null;
  if (/\b(cancel|never\s*mind|forget\s*it|stop|nothing|skip|no)\b/.test(s)) return "cancel";
  if (/\b(delete|remove|get\s*rid|clear|erase|bin\s*it)\b/.test(s)) return "delete";
  if (/\b(replace|swap|change\s*it|override|overwrite|instead)\b/.test(s)) return "replace";
  if (/\b(add|combine|total|sum|more|plus|together|stack|increase|increment|yes|yeah|yep)\b/.test(s)) return "add";
  return null;
}
